import React, { useEffect, useState, useRef, Suspense, lazy, useCallback } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Navigate,
  Outlet
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { jwtDecode } from "jwt-decode";

// Providers
import { SocketProvider, useSocket } from "./pages/contexts/SocketContext";
import { SessaoCompletaProvider } from "./pages/hooks/useSessaoCompleta";

// Utils / Auth
import { logout } from "./utils/auth";
import styles from "./components/Modals/Modal.module.css";
import {RefreshCw} from 'lucide-react';

// Guard
import PrivateRoute from "./routes/PrivateRoute";

// --- IMPORTS DAS PÁGINAS ---
import DocumentosGerenciar from "./pages/DocumentosGerenciar";
import Materia from "./pages/Materia";

// Função para tentar carregar o componente, forçando o F5 automático caso o chunk antigo não exista mais
const lazyComRetry = (importProvider) => {
  return lazy(async () => {
    const key = 'recarregou-por-erro-chunk';
    const jaRecarregou = JSON.parse(window.sessionStorage.getItem(key) || 'false');
    
    try {
      const componente = await importProvider();
      // Se carregou com sucesso, limpa a flag
      window.sessionStorage.setItem(key, 'false');
      return componente;
    } catch (error) {
      if (!jaRecarregou) {
        // Se deu erro e ainda não tentamos recarregar, salva a flag e força o F5
        window.sessionStorage.setItem(key, 'true');
        window.location.reload();
        // Retorna uma Promise que nunca resolve para segurar a tela enquanto recarrega
        return new Promise(() => {}); 
      }
      // Se já recarregou e o erro persiste, é um erro real, joga pro ErrorBoundary
      throw error;
    }
  });
};

// Lazy Loading (Agora usando a função com Retry)
const TooltipLazy = lazyComRetry(() => import("react-tooltip").then((m) => ({ default: m.Tooltip })));
const Login = lazyComRetry(() => import("./pages/Login"));
const LoginAdmin = lazyComRetry(() => import("./pages/LoginAdmin"));
const Config = lazyComRetry(() => import("./pages/Config"));
const Mensagens = lazyComRetry(() => import("./pages/Mensagens"));
const PainelControle = lazyComRetry(() => import("./pages/PainelControle"));
const ComissoesConfigurar = lazyComRetry(() => import("./components/ComissoesConfigurar"));
const ObsPainel = lazyComRetry(() => import("./components/obs/ObsPainel"));
const PainelSessao = lazyComRetry(() => import("./pages/PainelSessao"));
const ComissoesGerenciar = lazyComRetry(() => import("./pages/ComissoesGerenciar"));
const ComissaoDetalhes = lazyComRetry(() => import("./pages/ComissaoDetalhes"));
const UsuariosGerenciar = lazyComRetry(() => import("./pages/UsuariosGerenciar"));
const PautasGerenciar = lazyComRetry(() => import("./pages/PautasGerenciar"));
const LeisNormas = lazyComRetry(() => import("./pages/LeisNormas"));
const AtasGerenciar = lazyComRetry(() => import("./pages/AtasGerenciar"));
const SessoesGerenciar = lazyComRetry(() => import("./pages/SessoesGerenciar"));
const TransmissaoGerenciar = lazyComRetry(() => import("./pages/TransmissaoGerenciar"));
const CamaraMunicipal = lazyComRetry(() => import("./pages/CamaraMunicipal"));
const VotacoesGerenciar = lazyComRetry(() => import("./pages/VotacoesGerenciar"));
const CamaraConfig = lazyComRetry(() => import("./pages/CamaraConfig"));
const BackupGerenciar = lazyComRetry(() => import("./pages/BackupGerenciar"));
const HistoricoSistema = lazyComRetry(() => import("./pages/HistoricoSistema"));
const EstatisticasDashboard = lazyComRetry(() => import("./components/EstatisticasDashboard"));
const Hotsite = lazyComRetry(() => import("./pages/hotsite/Hotsite"));
const PautasExecutivo = lazyComRetry(() => import("./pages/PautasExecutivo"));
const CadastrarMaterias = lazyComRetry(() => import("./pages/CadastrarMaterias"));
const PlenarioVirtual = lazyComRetry(() => import("./pages/PlenarioVirtual"));
const PortalTransparencia = lazyComRetry(() => import("./pages/portal/PortalTransparencia"));
const NoticiasGerenciar = lazyComRetry(() => import("./pages/NoticiasGerenciar"));
const Logout = lazyComRetry(() => import("./pages/Logout"));

// ===== DEFINIÇÃO DE ROLES =====
const ROLES = {
  ADMIN: "admin",
  ROOT: "root",
  PRESIDENTE: "presidente",
  VEREADOR: "vereador",
  SECRETARIO: "secretario",
  SERVIDOR: "servidor",
  EXECUTIVO: "executivo",
  OBSPAINEL: "obspainel",
  PAINEL: "painel",
};

// Grupos de Acesso
const TODOS_USUARIOS = [ROLES.ADMIN, ROLES.ROOT, ROLES.PRESIDENTE, ROLES.VEREADOR, ROLES.SECRETARIO, ROLES.SERVIDOR];
const PRESI_ADMIN = [ROLES.ADMIN, ROLES.ROOT, ROLES.PRESIDENTE, ROLES.SERVIDOR, ROLES.SECRETARIO];
const EXECUTIVO_ROLE = [ROLES.EXECUTIVO];
const SECRETARIO_ROLE = [ROLES.ADMIN, ROLES.ROOT, ROLES.PRESIDENTE, ROLES.SECRETARIO, ROLES.SERVIDOR];

const ROTAS_PUBLICAS_PREFIX = ["/login", "/login-admin", "/site", "/camara-municipal", "/portal"];

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60000,
    },
  },
});

// Componente de Tratamento de Erro Global
class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error("Erro App:", err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className={styles.overlay}>
          <div className={styles.loadingContainer}>
            <p style={{color: '#333'}}>Ops! Algo deu errado.</p>
            <button className={styles.btnPrincipal}
              onClick={() => { localStorage.clear(); window.location.href = '/login'; }}
              style={{marginTop: 10, padding: '8px 16px', cursor: 'pointer'}}
            >
              Reiniciar Conexão
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const SuspenseFallback = () => (
  <div className={styles.overlay}>
    <div className={styles.loadingContainer}>
      <RefreshCw className={styles.spinner} size={24} style={{marginRight: 10}}/> 
      <span>Carregando Sistema...</span>
    </div>
  </div>
);

// Wrapper para Injetar SessaoCompleta
const SessaoLayout = () => {
  return (
    <SessaoCompletaProvider>
      <Outlet />
    </SessaoCompletaProvider>
  );
};

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const [verificando, setVerificando] = useState(true);
  const logoutTimer = useRef(null);
  const socket = useSocket();

  // 1. Identificador de rotas públicas
  const isPublicPath = useCallback(
    (path) => ROTAS_PUBLICAS_PREFIX.some((p) => path.startsWith(p)),
    []
  );

  // 2. Identificador de rotas de Auto Login (Painel e OBS)
  const isAutoLoginRoute = useCallback((path) => {
    return path.startsWith("/painelsessao") || path.startsWith("/obs");
  }, []);

  useEffect(() => {
    let isMounted = true;

    const verificarToken = () => {
      const isPublic = isPublicPath(location.pathname);
      const isAutoLogin = isAutoLoginRoute(location.pathname);
      const token = localStorage.getItem("token");

      // CASO 1: Sem token.
      if (!token) {
        if (!isPublic && !isAutoLogin) {
            logout();
            navigate("/login");
        }
        if (isMounted) setVerificando(false);
        return;
      }

      // CASO 2: Token existe. Validar integridade e expiração.
      try {
        const decoded = jwtDecode(token); 
        const now = Date.now() / 1000;

        // Verifica Expiração
        if (!decoded.exp || decoded.exp < now) {
            throw new Error("Token expirado");
        }

        // Configura Timer de Logout Automático
        const timeLeft = Math.max(0, (decoded.exp - now) * 1000);
        if (logoutTimer.current) clearTimeout(logoutTimer.current);
        
        logoutTimer.current = setTimeout(() => {
            console.log("⏰ Sessão expirada pelo timer.");
            if (!isAutoLogin) {
                logout();
                navigate("/login");
            } else {
                localStorage.removeItem("token");
            }
        }, timeLeft);

        // ===== [SEGURANÇA] FIREWALL DE CARGOS =====
        const userRole = (decoded.role || decoded.cargo || "").toLowerCase();

        const isVisualizacaoRole = [
          'obspainel', 'obs', 'observador',
          'painel', 'tv', 'telao'
        ].some(r => userRole.includes(r));

        if (isVisualizacaoRole) {
          const rotasPermitidas = ["/obs", "/painelsessao", "/login", "/logout"];
          const currentPath = location.pathname;
          
          const isPermitted = rotasPermitidas.some(prefix => currentPath.startsWith(prefix));

          if (!isPermitted) {
            console.warn(`⛔ Bloqueio de Segurança: Cargo '${userRole}' tentando acessar '${currentPath}'`);
            const target = userRole.includes('obs') ? "/obs" : "/painelsessao";
            navigate(target, { replace: true });
            return; 
          }
        }
        // ============================================

      } catch (error) {
        console.warn("⚠️ Auth Error:", error.message);
        
        if (isAutoLogin) {
           localStorage.removeItem("token");
        } else if (!isPublic) {
           logout();
           navigate("/login");
           return;
        }
      }
      
      if (isMounted) setVerificando(false);
    };

    verificarToken();

    return () => { 
      isMounted = false;
      if (logoutTimer.current) clearTimeout(logoutTimer.current); 
    };
  }, [location.pathname, navigate, isPublicPath, isAutoLoginRoute]);

  // Listener para logout em outras abas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "token" && !e.newValue && !isPublicPath(window.location.pathname) && !isAutoLoginRoute(window.location.pathname)) {
        navigate("/login", { replace: true });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [navigate, isPublicPath, isAutoLoginRoute]);

  // Listener para logout forçado via Socket (Chute remoto)
  useEffect(() => {
    if (!socket) return;
    const handleForceLogout = () => {
      console.log("⚠️ Recebido comando de logout forçado via Socket");
      logout();
      if (!isPublicPath(location.pathname)) {
        navigate("/login", { replace: true, state: { message: "Sessão encerrada remotamente." } });
      }
    };
    socket.on('force_logout_all', handleForceLogout);
    return () => socket.off('force_logout_all', handleForceLogout);
  }, [socket, navigate, location, isPublicPath]);

  if (verificando) return <SuspenseFallback />;

  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Routes>
        {/* ROTAS PÚBLICAS */}
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={<LoginAdmin />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/site" element={<Hotsite />} />
        <Route path="/camara-municipal" element={<CamaraMunicipal />} />
        <Route path="/portal" element={<PortalTransparencia />} />
        
        {/* ROTAS DE AUTO LOGIN (PAINEL/OBS) */}
        <Route path="/obs" element={<ObsPainel />} /> 
        <Route path="/painelsessao" element={<PainelSessao />} />
        <Route path="/painelsessao/:sessaoId" element={<PainelSessao />} />

        {/* ROTAS PROTEGIDAS (Backend validará o token) */}
        <Route element={<SessaoLayout />}>
            
            {/* GERAIS */}
            <Route element={<PrivateRoute rolesAllowed={TODOS_USUARIOS} />}>
              <Route path="/plenario" element={<PlenarioVirtual />} />
            </Route>

            {/* ADMIN/MESA */}
            <Route element={<PrivateRoute rolesAllowed={PRESI_ADMIN} />}>
              <Route path="/comissoes-configurar" element={<ComissoesConfigurar />} />
              <Route path="/gerenciar-transmissao" element={<TransmissaoGerenciar />} />
              <Route path="/usuarios" element={<UsuariosGerenciar />} />
              <Route path="/backups-gerenciar" element={<BackupGerenciar />} />
              <Route path="/historico" element={<HistoricoSistema />} />
              <Route path="/sessoes-anteriores" element={<SessoesGerenciar />} />
              <Route path="/config-camara" element={<CamaraConfig />} />
              <Route path="/configuracao" element={<Config />} />
            </Route>

            {/* SECRETARIA */}
            <Route element={<PrivateRoute rolesAllowed={SECRETARIO_ROLE} />}>
              <Route path="/noticias-gerenciar" element={<NoticiasGerenciar />} />
              <Route path="/comissoes" element={<ComissoesGerenciar />} />
              <Route path="/comissoes/:id" element={<ComissaoDetalhes />} />
              <Route path="/votacoes-gerenciar" element={<VotacoesGerenciar />} />
              <Route path="/atas-gerenciar" element={<AtasGerenciar />} />
              <Route path="/atas/gerar" element={<AtasGerenciar />} />
              <Route path="/atas/gerar/:ataId" element={<AtasGerenciar />} />
              <Route path="/pautas-gerenciar" element={<PautasGerenciar />} />
            </Route>

            {/* EXECUTIVO */}
            <Route element={<PrivateRoute rolesAllowed={EXECUTIVO_ROLE} />}>
              <Route path="/executivo" element={<PautasExecutivo />} />
            </Route>

        </Route>

        <Route path="*" element={<Navigate to="/login" />} />

        <Route path="/dashboard" element={<Navigate to="/plenario" />} />
        <Route path="/cadastrar-materia" element={<Navigate to="/plenario" />} />
        <Route path="/documentos" element={<Navigate to="/plenario" />} />
        <Route path="/normas" element={<Navigate to="/plenario" />} />
        <Route path="/materia/:id" element={<Navigate to="/plenario" />} />

        <Route path="/painel-controle" element={<Navigate to="/config-camara?tab=painelcontrole" />} />
        <Route path="/painel-controle/:sessaoId" element={<Navigate to="/config-camara?tab=painelcontrole" />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    // Hack para desbloquear AudioContext em navegadores modernos
    const desbloquearAudio = () => {
      const audio = new Audio("/sounds/item_novo.mp3");
      audio.volume = 0.0;
      audio.play().catch(() => {});
      window.removeEventListener("click", desbloquearAudio);
    };
    window.addEventListener("click", desbloquearAudio);
    return () => window.removeEventListener("click", desbloquearAudio);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <ErrorBoundary>
          <SocketProvider>
            <AppRoutes />
          </SocketProvider>
        </ErrorBoundary>

        <ToastContainer
          position="top-right"
          autoClose={2500}
          hideProgressBar={true}
          theme="light"
          style={{ top: 20, right: 20, zIndex: 999999}}
        />

        <Suspense fallback={null}>
          <TooltipLazy id="tooltip-default" place="top" />
        </Suspense>
      </Router>
    </QueryClientProvider>
  );
}

export default App;